import type { AddressObject } from '../types';
import { LAUNCH_TYPES } from './constants';

/**
 * Extract an HTTP status code from an unknown caught error.
 * Works with RTK Query rejections which have a top-level `status` property.
 */
export function getErrorStatusCode(error: unknown): number | undefined {
  if (
    error != null &&
    typeof error === 'object' &&
    'status' in error &&
    typeof error.status === 'number'
  ) {
    return error.status;
  }
  return undefined;
}

/**
 * Extract a human-readable message from an unknown caught error.
 */
export function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Unknown error';
}

interface SelectOptionLike {
  value?: string;
}

// Helper function to format address from address object
export const formatAddressFromObject = (
  addressObject: AddressObject | null | undefined,
): string => {
  if (!addressObject) return '';

  const addressParts: (string | undefined)[] = [];
  const { home, street, city, region, postal_code } = addressObject;

  // Handle street address part
  if (home && street) {
    addressParts.push(`${home} ${street}`);
  } else {
    addressParts.push(home || street);
  }

  // Add remaining address components
  if (city) addressParts.push(city);
  if (region) addressParts.push(region);
  if (postal_code) addressParts.push(postal_code);

  return addressParts.filter(Boolean).join(', ');
};

// Helper function to get value from react-select option
export const getSelectValue = (
  option: SelectOptionLike | string | null | undefined,
): string => {
  if (!option) return '';
  if (typeof option === 'string') return option;
  return option.value || '';
};

// Helper to check if coordinates are valid
export const isValidCoordinates = (
  latitude: number,
  longitude: number,
): boolean =>
  latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

// Helper to check if the launch type is test or standby
export const checkLaunchType = (
  launchType: string | null = null,
): string | false | null => {
  if (!launchType) return null;
  const lowerCaseType = launchType.toLowerCase();
  if (
    lowerCaseType === LAUNCH_TYPES.TEST.toLowerCase() ||
    lowerCaseType === LAUNCH_TYPES.STANDBY.toLowerCase()
  ) {
    return launchType;
  }
  return false;
};
